import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import logger from '@/lib/logger';

const FIELD = "w-full rounded-[6px] border border-[#DDE1E5] bg-white px-2.5 py-1.5 text-sm text-[#171A1D] focus:border-[#78B928] outline-none";

/**
 * Owner-authored decision framing for a scenario set.
 * The owner selects the preferred scenario — ISMSCalculator never recommends
 * one, and an empty selection must not imply a preference.
 */
export function DecisionContextCard({ set, onSaved }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    organisation: set.decision?.organisation || '',
    title: set.decision?.title || '',
    context: set.decision?.context || '',
    note: set.decision?.note || '',
    preferred_estimate_id: set.decision?.preferred_estimate_id || '',
  }));

  const update = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/scenarios/${set.group_id}/decision`, {
        organisation: form.organisation || null,
        title: form.title || null,
        context: form.context || null,
        note: form.note || null,
        preferred_estimate_id: form.preferred_estimate_id || null,
      }, { withCredentials: true });
      toast.success(t('scenarios.decisionSaved'));
      if (onSaved) onSaved(data);
    } catch (err) {
      logger.warn('[DecisionContextCard] save failed:', err?.message || err);
      toast.error(t('scenarios.decisionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const selectedName = set.estimates.find(e => e.estimate_id === form.preferred_estimate_id)?.name;

  return (
    <div className="border-t border-[#DDE1E5]" data-testid={`decision-context-${set.group_id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-[#F5F6F7] transition-colors duration-150"
        data-testid={`decision-toggle-${set.group_id}`}
      >
        <span className="min-w-0">
          <span className="block text-xs font-medium text-[#3A4249]">{t('scenarios.decision')}</span>
          <span className="block text-[11px] text-[#8A929B] truncate">
            {selectedName
              ? `${t('scenarios.selectedScenario')}: ${selectedName}`
              : t('scenarios.preferredNone')}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-[#66707A] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="px-5 pb-4 space-y-3" data-testid={`decision-form-${set.group_id}`}>
          <p className="text-xs text-[#66707A]">{t('scenarios.decisionIntro')}</p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.fieldOrganisation')}</span>
              <Input
                value={form.organisation}
                onChange={update('organisation')}
                maxLength={120}
                className="border-[#DDE1E5] h-9"
                data-testid={`decision-organisation-${set.group_id}`}
              />
            </label>
            <label className="block">
              <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.fieldTitle')}</span>
              <Input
                value={form.title}
                onChange={update('title')}
                maxLength={120}
                className="border-[#DDE1E5] h-9"
                data-testid={`decision-title-${set.group_id}`}
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.fieldContext')}</span>
            <Textarea
              value={form.context}
              onChange={update('context')}
              maxLength={800}
              rows={2}
              className="border-[#DDE1E5] text-sm"
              data-testid={`decision-context-input-${set.group_id}`}
            />
          </label>

          <label className="block">
            <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.fieldNote')}</span>
            <Textarea
              value={form.note}
              onChange={update('note')}
              maxLength={1000}
              rows={3}
              className="border-[#DDE1E5] text-sm"
              data-testid={`decision-note-${set.group_id}`}
            />
          </label>

          <label className="block">
            <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.fieldPreferred')}</span>
            <select
              value={form.preferred_estimate_id}
              onChange={update('preferred_estimate_id')}
              className={FIELD}
              data-testid={`decision-preferred-${set.group_id}`}
            >
              <option value="">{t('scenarios.preferredNone')}</option>
              {set.estimates.map(estimate => (
                <option key={estimate.estimate_id} value={estimate.estimate_id}>{estimate.name}</option>
              ))}
            </select>
          </label>

          <Button
            onClick={save}
            disabled={saving}
            className="h-9 bg-[#527F19] hover:bg-[#3F6114] text-white rounded-md"
            data-testid={`decision-save-${set.group_id}`}
          >
            {saving
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
              : <Save className="w-4 h-4 mr-2" aria-hidden="true" />}
            {t('scenarios.saveDecision')}
          </Button>
        </div>
      )}
    </div>
  );
}
